import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  CREATE_ASSET_BY_TYPE,
  DELETE_ASSET_BY_ID,
  GET_ASSET_BY_ID,
  GET_ASSET_BY_TYPE,
  UPDATE_ASSET,
  FORCE_DELETE_ASSET,
  GET_ASSET_BY_TYPE_PAGINATED,
  GET_ASSET_ID_NAME_BY_TYPE,
} from '../graphql/asset.operations';
import { FetchPolicy } from '@apollo/client';
import { FIND_ASSET_BY_RELATIONSHIP, GET_ASSETS_BY_RELATIONSHIPS_BY_ID, GET_ASSETS_BY_RELATIONSHIPS_BY_TYPE } from '../graphql/data-scope.operation';

@Injectable({
  providedIn: 'root',
})
export class AssetsApiService {
  private readonly apollo = inject(Apollo);

  public getAssetByType(
    type: string,
    fetchPolicy: FetchPolicy = 'cache-first',
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_ASSET_BY_TYPE,
      variables: { assetType: type },
      fetchPolicy: fetchPolicy,
    });
  }

  public getAssetByTypePaginated(
    type: string,
    page: number,
    limit: number,
    connectionId?: string,
    searchCriteria?: string,
    filters?: any,
    useCache: boolean = true
  ): Observable<any> {
    const fetchPolicy: FetchPolicy = useCache ? 'cache-first' : 'network-only';
    const variables: any = { assetType: type, page, limit };
    if (connectionId && connectionId.trim() !== '') {
      variables.connectionId = connectionId;
    }
    if (searchCriteria && searchCriteria.trim() !== '') {
      variables.searchCriteria = searchCriteria;
    }
    if (filters && Object.keys(filters).length > 0) {
      variables.filters = filters;
    }
    return this.apollo.query<any>({
      query: GET_ASSET_BY_TYPE_PAGINATED,
      variables: variables,
      fetchPolicy: fetchPolicy,
    });
  }

  public getAssetById(
    assetId: string,
    fetchPolicy: FetchPolicy = 'cache-first',
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_ASSET_BY_ID,
      variables: { assetId },
      fetchPolicy: fetchPolicy,
    });
  }

  public createAsset(asset: any): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: CREATE_ASSET_BY_TYPE,
      variables: { createAssetData: asset },
    });
  }

  public updateAsset(asset: any): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_ASSET,
      variables: { updateAssetData: asset },
    });
  }

  public deleteAsset(assetId: any): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_ASSET_BY_ID,
      variables: { assetId },
    });
  }

  public getAssetsByRelationshipsById(assetId: string, relatedAssetTypes: string[], fetchPolicy: FetchPolicy = 'network-only'): Observable<any> {
    return this.apollo.query({
      query: GET_ASSETS_BY_RELATIONSHIPS_BY_ID,
      variables: {
        assetId,
        relatedAssetTypes,
      },
      fetchPolicy: fetchPolicy,
    }).pipe(
      map((result: any) => result.data.getAssetsByRelationships)
    );
  }

  public getAssetsByRelationshipsByType(assetTypes: string[], relatedAssetTypes: string[], fetchPolicy: FetchPolicy = 'cache-first'): Observable<any> {
    return this.apollo.query({
      query: GET_ASSETS_BY_RELATIONSHIPS_BY_TYPE,
      variables: {
        assetTypes,
        relatedAssetTypes,
      },
      fetchPolicy: fetchPolicy,
    }).pipe(
      map((result: any) => result.data.getAssetsByRelationships)
    );
  }

  public findAssetsByRelationship(primaryAssetId: string[], secondaryAssetType: string[], matchAnyPrimary = false, fetchPolicy: FetchPolicy = 'network-only'): Observable<any> {
    return this.apollo.query({
      query: FIND_ASSET_BY_RELATIONSHIP,
      variables: {
        primaryAssetId,
        secondaryAssetType,
        matchAnyPrimary
      },
      fetchPolicy: fetchPolicy,
    });
  }

  public forceDeleteAsset(deleteAssetInput: { id: string; type: string }): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: FORCE_DELETE_ASSET,
      variables: { deleteAssetInput },
    });
  }

  public getAssetIdNameByType(
    type: string,
    fetchPolicy: FetchPolicy = 'cache-first',
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_ASSET_ID_NAME_BY_TYPE,
      variables: { assetType: type },
      fetchPolicy: fetchPolicy,
    });
  }
}
